import classNames from 'classnames';
import { type ChangeEvent, type FunctionComponent, type JSX } from 'react';
import React, { useState } from 'react';
import type BEMHelper from 'react-bem-helper';
import * as accents from 'remove-accents';

import { type ColorKey } from '../../stylekit';
import { COLORS } from '../../stylekit';
import { formClasses } from '../../utils/BEM';
import { useId } from '../../utils/idGenerator';
import FieldErrorMessage from './FieldErrorMessage';
import { type FormErrorLike } from './FormErrors';
import { FORM_SEVERITY_CAUTION, getFormErrorSeverity } from './FormErrors';
import { type Suggestable } from './Suggester';
import Suggester from './Suggester';

export interface GenericFieldProps {
    label: string | null;
    tinyLabel?: string;
    placeholder?: string;
    idPrefix?: string;
    defaultSmall?: boolean;
    defaultTiny?: boolean;
    onChange?: (newValue: string) => void;
    onChangeComplete?: (newValue: string) => void;
    control: (props: PropsPassedToControl) => JSX.Element;
    disabled?: boolean;
    controlModifiers?: BEMHelper.PredicateSet;
    fieldModifiers?: BEMHelper.PredicateSet;
    inputModifiers?: BEMHelper.PredicateSet;
    autoFocus?: boolean;
    value?: string;
    suggestions?: Suggestable[];
    suggestionsNegativeTabIndex?: boolean;
    errorClass?: boolean;
    error?: FormErrorLike;
    forceErrorDisplay?: boolean;
    autoComplete?: 'current-password' | 'off' | 'on' | 'username';
    name?: string;
    bgColor?: ColorKey;
    pointerEventsOff?: boolean;
    labelSuffixComponent?: React.ReactNode;
    txtColor?: ColorKey;
}

export type PropsPassedToControl = {
    className: string;
    value: string;
    id: string;
    onChange: (evt: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
    autoFocus?: boolean;
    disabled?: boolean;
    error?: FormErrorLike;
    placeholder?: string;
    autoComplete?: string;
    name?: string;
    style?: React.CSSProperties;
};

const GenericField: FunctionComponent<GenericFieldProps> = (props) => {
    const [innerValue, setInnerValue] = useState(props.value || '');
    const value = props.value === undefined ? innerValue : props.value;
    const [mutated, setMutated] = useState(false);
    const [focused, setFocused] = useState(false);
    const [focusedCount, setFocusedCount] = useState(0);
    const [blurredCount, setBlurredCount] = useState(0);
    const [touched, setTouched] = useState(false);
    const onChange = (evt: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        onNewValue(evt.target.value);
    };
    const onNewValue = (newValue: string) => {
        setInnerValue(newValue);
        if (!mutated) {
            setMutated(true);
        }
        props.onChange?.(newValue);
    };
    const onFocus = () => {
        setFocused(true);
        setFocusedCount(focusedCount + 1);
        setTouched(true);
    };
    const onBlur = () => {
        setFocused(false);
        setBlurredCount(blurredCount + 1);
        props.onChangeComplete?.(value);
    };
    const [id] = useId(props.idPrefix);
    const tiny = !!(props.defaultTiny && !focused && !innerValue);
    const small = !!(props.defaultSmall && !focused && !innerValue);
    const label = tiny && props.tinyLabel ? props.tinyLabel : props.label;
    const canShowErrors = blurredCount > 0 || props.forceErrorDisplay;
    const showErrors = canShowErrors && (props.error || props.errorClass);
    const errorIsCaution = props.error && getFormErrorSeverity(props.error) === FORM_SEVERITY_CAUTION;
    const modifiers = {
        ...props.fieldModifiers,
        mutated,
        focused,
        touched,
        tiny,
        small,
        'no-label': label === null,
        error: !!showErrors,
        'error-caution': !!(showErrors && errorIsCaution),
    };

    const onSuggestionSelected = (suggestion: string) => {
        setTouched(true);
        onNewValue(suggestion);
        props.onChangeComplete?.(suggestion);
    };

    return (
        <div
            className={classNames({ ...formClasses('field', { ...modifiers }) }.className, {
                'pe-none': props.pointerEventsOff,
            })}
            data-has-error={props.error ? getFormErrorSeverity(props.error) : undefined}
        >
            {label !== null && (
                <div {...formClasses('field-label')}>
                    <label htmlFor={id}>{label}</label>
                    {props.labelSuffixComponent}
                </div>
            )}
            <div {...formClasses('field-input', props.inputModifiers)}>
                {props.control({
                    ...formClasses('control', { ...modifiers, ...props.controlModifiers }),

                    onChange,
                    value,
                    onFocus,
                    onBlur,
                    id,
                    autoFocus: props.autoFocus,
                    disabled: props.disabled,
                    placeholder: props.placeholder,
                    autoComplete: props.autoComplete,
                    name: props.name,
                    style: {
                        backgroundColor: props.bgColor ? COLORS[props.bgColor ?? ''] : undefined,
                        color: props.txtColor ? COLORS[props.txtColor ?? ''] : undefined,
                    },
                })}
                {canShowErrors ? <FieldErrorMessage error={props.error} /> : null}
                {props.suggestions && props.suggestions.length > 0 ? (
                    <Suggester
                        negativeTabIndex={props.suggestionsNegativeTabIndex}
                        options={props.suggestions}
                        onSelected={onSuggestionSelected}
                    />
                ) : null}
            </div>
        </div>
    );
};

export const standardSuggesterFilter = (currentValue: string, availableValues: string[]): string[] => {
    if (currentValue.length < 2) {
        return [];
    }
    const inputClean = accents.remove(currentValue).toLowerCase();
    const regex = new RegExp(`\\b${inputClean}`, 'i');

    return availableValues.filter((av) => {
        const clean = accents.remove(av).toLowerCase();

        return clean.match(regex) && av !== currentValue;
    });
};

export default GenericField;
